import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './JobApplicants.css';
import {
  ArrowLeft,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Users,
  SlidersHorizontal,
  ExternalLink,
  Loader2,
  GraduationCap,
  X,
  UserCheck,
  Briefcase,
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const JobApplicants = ({ jobId, jobRole, collegeUserId, onBack, onViewProfile }) => {
  const [applicants, setApplicants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search, Sort, Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);

  /* ── Fetch ───────────────────────────────────────────────── */
  useEffect(() => {
    const fetchApplicants = async () => {
      if (!jobId || !collegeUserId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/college/job-applicants/${jobId}/${collegeUserId}`
        );
        setApplicants(response.data);
      } catch (error) {
        console.error('Failed to fetch applicants:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchApplicants();
  }, [jobId, collegeUserId]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortField, sortDir]);

  /* ── Sort Handler ────────────────────────────────────────── */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  /* ── Processed List ──────────────────────────────────────── */
  const processed = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    let list = applicants.filter((s) => {
      if (!q) return true;
      return (
        (s.fullName || '').toLowerCase().includes(q) ||
        (s.rollNo || '').toLowerCase().includes(q) ||
        (s.personalEmail || '').toLowerCase().includes(q) ||
        (s.phoneNumber || '').toLowerCase().includes(q)
      );
    });

    if (sortField) {
      list = [...list].sort((a, b) => {
        if (sortField === 'cgpa') {
          const aV = parseFloat(a.currentCGPA) || 0;
          const bV = parseFloat(b.currentCGPA) || 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'tenth') {
          const aV = parseFloat(a.tenthPercentile) || 0;
          const bV = parseFloat(b.tenthPercentile) || 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'twelfth') {
          const aV = parseFloat(a.twelfthPercentile) || 0;
          const bV = parseFloat(b.twelfthPercentile) || 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'rollNo') {
          const aV = (a.rollNo || '').toLowerCase();
          const bV = (b.rollNo || '').toLowerCase();
          return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
        }
        if (sortField === 'name') {
          const aV = (a.fullName || '').toLowerCase();
          const bV = (b.fullName || '').toLowerCase();
          return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
        }
        return 0;
      });
    }

    return list;
  }, [applicants, searchQuery, sortField, sortDir]);

  /* ── Pagination ──────────────────────────────────────────── */
  const totalPages = Math.ceil(processed.length / ITEMS_PER_PAGE);
  const paginated = processed.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getPageNums = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  /* ── Sort Icon ───────────────────────────────────────────── */
  const SortIcon = ({ field }) => {
    const active = sortField === field;
    return (
      <span className="ja-sort-icons">
        <ChevronUp size={11} className={active && sortDir === 'asc' ? 'ja-si-on' : ''} />
        <ChevronDown size={11} className={active && sortDir === 'desc' ? 'ja-si-on' : ''} />
      </span>
    );
  };

  /* ── Get Initials ────────────────────────────────────────── */
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  };

  /* ── Loading ─────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="ja-root">
        <div className="ja-loading">
          <div className="ja-loading-card">
            <Loader2 className="ja-spin" size={36} />
            <span>Loading applicants…</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div className="ja-root">
      {/* Background blobs */}
      <div className="ja-bg-blob ja-blob-1" />
      <div className="ja-bg-blob ja-blob-2" />

      {/* ── Back Button ── */}
      <button className="ja-back-btn" onClick={onBack}>
        <ArrowLeft size={16} /> Back to Jobs
      </button>

      {/* ── Header ── */}
      <div className="ja-header">
        <div className="ja-header-left">
          <div className="ja-title-icon">
            <UserCheck size={26} />
          </div>
          <div>
            <h1 className="ja-title">Job Applicants</h1>
            <p className="ja-subtitle">
              <Briefcase size={13} /> {jobRole}
            </p>
          </div>
        </div>
        <span className="ja-count-badge">
          <Users size={14} />
          {processed.length} applicant{processed.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Controls ── */}
      <div className="ja-controls">
        {/* Search */}
        <div className="ja-search-wrap">
          <Search size={16} className="ja-search-icon" />
          <input
            className="ja-search"
            type="text"
            placeholder="Search by name, roll no, email or phone…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="ja-clear-btn" onClick={() => setSearchQuery('')}>
              <X size={12} />
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="ja-sort-bar">
          <span className="ja-sort-label">
            <SlidersHorizontal size={13} /> Sort by
          </span>
          <button
            className={`ja-sort-btn ${sortField === 'name' ? 'active' : ''}`}
            onClick={() => handleSort('name')}
          >
            Name <SortIcon field="name" />
          </button>
          <button
            className={`ja-sort-btn ${sortField === 'rollNo' ? 'active' : ''}`}
            onClick={() => handleSort('rollNo')}
          >
            Roll No <SortIcon field="rollNo" />
          </button>
          <button
            className={`ja-sort-btn ${sortField === 'cgpa' ? 'active ja-cgpa' : ''}`}
            onClick={() => handleSort('cgpa')}
          >
            CGPA <SortIcon field="cgpa" />
          </button>
          <button
            className={`ja-sort-btn ${sortField === 'tenth' ? 'active ja-tenth' : ''}`}
            onClick={() => handleSort('tenth')}
          >
            10th % <SortIcon field="tenth" />
          </button>
          <button
            className={`ja-sort-btn ${sortField === 'twelfth' ? 'active ja-twelfth' : ''}`}
            onClick={() => handleSort('twelfth')}
          >
            12th % <SortIcon field="twelfth" />
          </button>
        </div>
      </div>

      {/* Showing */}
      <p className="ja-showing">
        {searchQuery
          ? `Showing ${paginated.length} of ${processed.length} results for "${searchQuery}"`
          : `Showing ${paginated.length} of ${processed.length} applicants`}
      </p>

      {/* ── Table Card ── */}
      <div className="ja-card">
        <div className="ja-table-wrap">
          <table className="ja-table">
            <thead>
              <tr>
                <th className="ja-th-num">#</th>
                <th className="ja-th-sort" onClick={() => handleSort('name')}>
                  Student Name <SortIcon field="name" />
                </th>
                <th className="ja-th-sort" onClick={() => handleSort('rollNo')}>
                  Roll No <SortIcon field="rollNo" />
                </th>
                <th>Email</th>
                <th>Phone</th>
                <th className="ja-th-sort" onClick={() => handleSort('cgpa')}>
                  CGPA <SortIcon field="cgpa" />
                </th>
                <th className="ja-th-sort" onClick={() => handleSort('tenth')}>
                  10th % <SortIcon field="tenth" />
                </th>
                <th className="ja-th-sort" onClick={() => handleSort('twelfth')}>
                  12th % <SortIcon field="twelfth" />
                </th>
                <th className="ja-th-right">Profile</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="9" className="ja-empty">
                    <GraduationCap size={40} className="ja-empty-icon" />
                    <p className="ja-empty-title">
                      {searchQuery
                        ? `No applicants match "${searchQuery}"`
                        : 'No students from your college have applied yet'}
                    </p>
                    <p className="ja-empty-sub">
                      Check back later or adjust your search
                    </p>
                  </td>
                </tr>
              ) : (
                paginated.map((student, idx) => (
                  <tr key={student._id} className="ja-row">
                    <td className="ja-td-num">
                      {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                    </td>
                    <td className="ja-td-name">
                      <div className="ja-name-avatar">
                        {getInitials(student.fullName)}
                      </div>
                      {student.fullName}
                    </td>
                    <td className="ja-td-mono">{student.rollNo || 'N/A'}</td>
                    <td className="ja-td-secondary">{student.personalEmail || 'N/A'}</td>
                    <td className="ja-td-secondary">{student.phoneNumber || 'N/A'}</td>
                    <td>
                      {student.currentCGPA ? (
                        <span className="ja-cgpa-badge">{student.currentCGPA}</span>
                      ) : (
                        <span className="ja-td-dim">—</span>
                      )}
                    </td>
                    <td>
                      {student.tenthPercentile ? (
                        <span className="ja-score-badge ja-score-teal">
                          {student.tenthPercentile}
                        </span>
                      ) : (
                        <span className="ja-td-dim">—</span>
                      )}
                    </td>
                    <td>
                      {student.twelfthPercentile ? (
                        <span className="ja-score-badge ja-score-purple">
                          {student.twelfthPercentile}
                        </span>
                      ) : (
                        <span className="ja-td-dim">—</span>
                      )}
                    </td>
                    <td className="ja-td-right">
                      <button
                        className="ja-view-btn"
                        onClick={() => onViewProfile(student.userId)}
                      >
                        <ExternalLink size={13} /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="ja-pagination">
            <span className="ja-page-info">
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </span>
            <div className="ja-page-controls">
              <button
                className="ja-page-btn nav"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNums().map((p) => (
                <button
                  key={p}
                  className={`ja-page-btn ${currentPage === p ? 'active' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="ja-page-btn nav"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApplicants;